// Field — label + control + message wrapper. Wrap any input/select/
// textarea. Shows an error row (with icon) when `error` is set, else `hint`.

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

export interface FieldProps {
  label?: string;
  /** id of the control this label points to. */
  htmlFor?: string;
  /** Error message — takes precedence over `hint` and turns the row red. */
  error?: string;
  /** Helper text shown below the control when there is no error. */
  hint?: string;
  required?: boolean;
  /** Renders a muted "· optional" tag after the label (mutually exclusive with `required`). */
  optional?: boolean;
  /** Custom muted marker after the label (e.g. a lock icon + "Automatic"). */
  labelTag?: ReactNode;
  className?: string;
  children: ReactNode;
}

const Field = ({
  label,
  htmlFor,
  error,
  hint,
  required,
  optional,
  labelTag,
  className = '',
  children,
}: FieldProps) => {
  const { t } = useTranslation();
  const classes = ['app-field', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {label && (
        <label className='app-field__label' htmlFor={htmlFor}>
          {label}
          {required && <span className='app-field__required'> *</span>}
          {optional && !required && (
            <span className='app-field__optional'> · {t('field_optional')}</span>
          )}
          {labelTag && <span className='app-field__label-tag'>{labelTag}</span>}
        </label>
      )}
      {children}
      {error ? (
        <span className='app-field__error' role='alert'>
          <Icon name='circle-alert' size={15} aria-hidden='true' />
          {error}
        </span>
      ) : (
        hint && <span className='app-field__hint'>{hint}</span>
      )}
    </div>
  );
};

export default Field;
